// src/services/userService.js
import api from './api';

const USER_TOKEN_KEY = 'userAccessToken';
const USER_REFRESH_KEY = 'userRefreshToken';
const USER_DATA_KEY = 'userData';

export const userService = {
  // 회원가입
  async register(data) {
    const response = await api.post('/users/register', data);
    const { access_token, refresh_token, user } = response.data;
    this._saveTokens(access_token, refresh_token);
    this._saveUser(user);
    return response.data;
  },

  // 로그인
  async login(email, password) {
    const response = await api.post('/users/login', { email, password });
    const { access_token, refresh_token, user } = response.data;
    this._saveTokens(access_token, refresh_token);
    this._saveUser(user);
    return response.data;
  },

  // 로그아웃
  logout() {
    // 서버 세션 폐기 (best-effort)
    const refreshToken = localStorage.getItem(USER_REFRESH_KEY);
    if (refreshToken) api.post('/users/logout', { refresh_token: refreshToken }).catch(() => {});
    localStorage.removeItem(USER_TOKEN_KEY);
    localStorage.removeItem(USER_REFRESH_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  },

  // 토큰 갱신
  async refreshToken() {
    const refreshToken = localStorage.getItem(USER_REFRESH_KEY);
    if (!refreshToken) return null;

    try {
      // 백엔드는 body(JSON) 로 받음 — 쿼리스트링 전달 시 422
      const response = await api.post('/users/refresh', { refresh_token: refreshToken });
      const { access_token, refresh_token: newRefresh, user } = response.data;
      this._saveTokens(access_token, newRefresh);
      this._saveUser(user);
      return response.data;
    } catch (error) {
      this.logout();
      return null;
    }
  },

  // 내 정보 조회
  async getMe() {
    const response = await api.get('/users/me', {
      headers: this._getAuthHeader()
    });
    this._saveUser(response.data);
    return response.data;
  },

  // 내 정보 수정
  async updateMe(data) {
    const response = await api.put('/users/me', data, {
      headers: this._getAuthHeader()
    });
    this._saveUser(response.data);
    return response.data;
  },

  // 비밀번호 변경
  async changePassword(currentPassword, newPassword) {
    const response = await api.post('/users/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: this._getAuthHeader()
    });
    return response.data;
  },

  // 비밀번호 재설정 요청
  async requestPasswordReset(email) {
    const response = await api.post('/users/password-reset/request', { email });
    return response.data;
  },

  // 비밀번호 재설정 토큰 검증
  async verifyResetToken(token) {
    const response = await api.post('/users/password-reset/verify', { token });
    return response.data;
  },

  // 비밀번호 재설정 확정
  async confirmPasswordReset(token, newPassword) {
    const response = await api.post('/users/password-reset/confirm', {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  // 회원 탈퇴 (NEW-30)
  async withdraw() {
    await api.delete('/users/me', { headers: this._getAuthHeader() });
  },

  // 내 주문 목록 조회
  async getMyOrders(skip = 0, limit = 20) {
    const response = await api.get('/users/me/orders', {
      params: { skip, limit },
      headers: this._getAuthHeader()
    });
    return response.data;
  },

  // 내 주문 상세 조회
  async getMyOrderDetail(orderNo) {
    const response = await api.get(`/users/me/orders/${orderNo}`, {
      headers: this._getAuthHeader()
    });
    return response.data;
  },

  // 내 티켓 목록 조회
  async getMyTickets(skip = 0, limit = 50) {
    const response = await api.get('/users/me/tickets', {
      params: { skip, limit },
      headers: this._getAuthHeader()
    });
    return response.data;
  },

  // 로그인 상태 확인
  isAuthenticated() {
    return !!localStorage.getItem(USER_TOKEN_KEY);
  },

  // 저장된 사용자 정보 가져오기
  getUser() {
    const userData = localStorage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  },

  // 토큰 가져오기
  getToken() {
    return localStorage.getItem(USER_TOKEN_KEY);
  },

  // 내부 헬퍼 함수들
  _saveTokens(accessToken, refreshToken) {
    localStorage.setItem(USER_TOKEN_KEY, accessToken);
    localStorage.setItem(USER_REFRESH_KEY, refreshToken);
  },

  _saveUser(user) {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
  },

  _getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
};

export default userService;
